import {Button, EmptyStatus, IconButton, Separator} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {t} from './catalogueUi';

export type DraftRecordListRow = {
  id: string;
  name: string;
  subtitle?: string;
  leading?: React.ReactNode;
  values?: React.ReactNode[];
  /** A backend rejection of this row; the row stays editable while locked so it can be fixed. */
  error?: string;
  /** The backend refused to create this row, so it can still be removed while locked. */
  isRemovable?: boolean;
};

type DraftRecordListProps = {
  title: string;
  addLabel: string;
  emptyHeadline: string;
  emptySubheadline: string;
  rows: DraftRecordListRow[];
  isLocked: boolean;
  onAdd: () => void;
  onEdit: (id: string) => void;
  onRemove: (id: string) => void;
};

export function DraftRecordList(props: DraftRecordListProps) {
  return (
    <VStack spacing={4}>
      <Show when={props.rows.length > 0}>
        <HStack align="center" justify="space-between" spacing={4}>
          <Text alternative>{props.title}</Text>
          <Button
            variant="secondary"
            leftIcon="content/add"
            aria-label={props.addLabel}
            title={props.addLabel}
            isDisabled={props.isLocked}
            onClick={props.onAdd}
          />
        </HStack>
      </Show>
      {props.rows.length === 0 ? (
        <EmptyStatus
          headline={props.emptyHeadline}
          subheadline={props.emptySubheadline}
          action={{
            variant: 'secondary',
            title: props.addLabel,
            isDisabled: props.isLocked,
            onClick: props.onAdd,
          }}
        />
      ) : (
        <VStack spacing={0}>
          {props.rows.map((row, index) => (
            <Box key={row.id}>
              <Show when={index > 0}>
                <Separator spacing={0} />
              </Show>
              <Box paddingVertical={3}>
                <HStack align="center" justify="space-between" spacing={4} wrap>
                  <HStack align="center" spacing={3} grow={1}>
                    <Show when={row.leading}>{row.leading}</Show>
                    <VStack spacing={1}>
                      <Text alternative>{row.name}</Text>
                      <Show when={row.subtitle}>
                        <Text size="small" color="secondary">
                          {row.subtitle}
                        </Text>
                      </Show>
                      <Show when={isNotNilOrEmpty(row.error)}>
                        <Text size="small" color="danger">
                          {row.error}
                        </Text>
                      </Show>
                    </VStack>
                  </HStack>
                  <HStack align="center" justify="flex-end" spacing={4} wrap>
                    {row.values?.slice(0, 3).map((value, valueIndex) => (
                      // eslint-disable-next-line react/no-array-index-key -- Pre-existing violation
                      <Box key={`${row.id}-value-${valueIndex}`}>{value}</Box>
                    ))}
                    <Button
                      variant="link"
                      title={t('general.actions.edit', 'Edit')}
                      // The row already says the name; repeating it visually reads as part of the
                      // record. Screen readers still get the name that tells the rows apart.
                      aria-label={t('general.actions.editNamed', 'Edit {name}', {name: row.name})}
                      isDisabled={props.isLocked && !row.error}
                      onClick={() => props.onEdit(row.id)}
                    />
                    <IconButton
                      icon="action/delete"
                      severity="danger"
                      aria-label={t('general.actions.removeNamed', 'Remove {name}', {
                        name: row.name,
                      })}
                      isDisabled={props.isLocked && !row.isRemovable}
                      onClick={() => props.onRemove(row.id)}
                    />
                  </HStack>
                </HStack>
              </Box>
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
}
